/* eslint-disable camelcase */
import _ from 'lodash'
import stringify from 'fast-safe-stringify'
import { hashCode } from '@/util/string'
import { repeatKeys } from '@/util/query-params'
import { getVisitorId } from '@/util/visitor-id'

const createApi = function ({ store, $axios }) {
    const api = {
        $pendingGETPromises: {},
        $pendingSources: {},
        $cancelPending () {
            for (const key in api.$pendingSources) {
                api.$pendingSources[key].cancel('Request canceled')
                delete api.$pendingSources[key]
            }
        },
        async $send (retries, method, ...args) {
            if (typeof retries === 'string') {
                args.unshift(method)
                method = retries
                retries = 0
            }
            const uri = args[0]
            const hashed = hashCode(stringify({ method, args }))
            const sourcePrefix = `${method}::${uri}`
            const sourceKey = `${sourcePrefix}__${hashed}`

            try {
                const source = $axios.CancelToken.source()
                const cancelToken = source.token
                api.$pendingSources[sourceKey] = source
                const ret = await $axios[method](...[...args, { cancelToken }])
                // const ret = await $axios[method](...args)
                const d = ret?.data || ret
                if (d && _.isObject(d)) {
                    d.from_cache = !!parseInt(ret.headers['from-cache']) || !!d.from_cache
                }
                return d
            } catch (e) {
                if ($axios.isCancel(e)) {
                    console.log('[app] request canceled', e.message)
                } else if (e.response && (e.response.status === 504 || e.response.status === 502) && retries < 3) {
                    console.warn(`[app] caught error ${e.response.status}, retrying...`, e)
                    return api.$send(retries + 1, method, ...args)
                } else if (e.response?.status === 403 &&
                           e.response?.data?.detail?.code === 'identity_verification_required') {
                    // The safety net under middleware/identityVerified.js. The
                    // middleware handles navigations; this catches everything
                    // else — a fetch on an already-open page, a component
                    // calling the API directly, a session that went pending
                    // mid-visit. The server refused the request, so nothing
                    // leaked; this only gets the client to the checkpoint
                    // instead of leaving them on a broken page.
                    if (process.client && window.location.pathname !== '/verify') {
                        const next = encodeURIComponent(
                            window.location.pathname + window.location.search,
                        )
                        // The caller still sees the error below, and its
                        // alert would flash up while the page is leaving.
                        store.commit('leavingForVerify', true)
                        window.location.assign(`/verify?next=${next}`)
                    }
                    throw store.getters.parseError(e)
                } else {
                    throw store.getters.parseError(e)
                }
            } finally {
                setTimeout(() => {
                    delete api.$pendingSources[sourceKey]
                })
            }
        },
        $fget (...args) {
            return api.$send('get', ...args)
        },
        $get (...args) {
            // for GET calls, if many are made at the "same" time,
            // only make the first, make the rest wait for its response
            const hashed = hashCode(stringify(args))
            if (api.$pendingGETPromises[hashed]) {
                api.$pendingGETPromises[hashed].count++
                console.warn('[app] back-to-back identical unfinished GET requests, ' +
                    "I'm just going to send one of them, the latter will wait and get the same results, " +
                    `Count=${api.$pendingGETPromises[hashed].count}, index=${api.$pendingGETPromises[hashed].count - 1}, args=${[...args]}`)
                return new Promise((resolve, reject) => {
                    api.$pendingGETPromises[hashed].promise.then((d) => {
                        resolve(_.cloneDeep(d))
                    }).catch(reject)
                })
            }
            const p = api.$send('get', ...args).finally(() => delete api.$pendingGETPromises[hashed])
            api.$pendingGETPromises[hashed] = {
                promise: p,
                count: 1
            }
            return p
        },
        $post (...args) {
            return api.$send('post', ...args)
        },
        $put (...args) {
            return api.$send('put', ...args)
        },
        $patch (...args) {
            return api.$send('patch', ...args)
        },
        $delete (...args) {
            return api.$send('delete', ...args)
        },
        getState () {
            return api.$get('/api/accounts/state/')
        },
        register (data) {
            return api.$post('/api/accounts/register/', data)
        },
        login (data) {
            return api.$post('/api/accounts/login/', data)
        },
        logout () {
            return api.$post('/api/accounts/logout/')
        },
        onEdge () {
            return api.$post('/api/accounts/on-edge/')
        },
        offEdge () {
            return api.$post('/api/accounts/off-edge/')
        },
        changePassword (data) {
            return api.$post('/api/accounts/change-password/', data)
        },
        resetPasswordStart (data) {
            return api.$post('/api/accounts/reset-password/start/', data)
        },
        resetPasswordFinish (data) {
            return api.$post('/api/accounts/reset-password/finish/', data)
        },
        clientIntake (data) {
            return api.$post('/api/accounts/intake/', data)
        },
        getMagicRedirectUrl (token, redirect) {
            return `${store.getters.hostUrl}/api/accounts/magic_redirect/${token}/${redirect ? encodeURIComponent(redirect) : ''}`
        },
        getPasswordResetUrl (token) {
            return `${store.getters.effectiveHostUrl}/reset-password/${token}`
        },
        getVerifyEmailUrl (token) {
            return `${store.getters.hostUrl}/api/accounts/email-verify/finish/${token}/`
        },
        magicRedirect (token, redirect) {
            return api.$get(api.getMagicRedirectUrl(token, redirect))
        },
        emailVerifyStart () {
            return api.$post('/api/accounts/email-verify/start/')
        },
        sendEmail (data) {
            return api.$post('/api/accounts/send-email/', data)
        },
        sendLoginLink (data) {
            return api.$post('/api/accounts/send-magic-link/', data)
        },
        sendSupportEmail (data) {
            return api.$post('/api/accounts/send-group-support-email/', data)
        },
        renderEmail (data) {
            return api.$post('/api/extras/render-email/', data)
        },
        emailPreviewUsers () {
            return api.$get('/api/extras/email-preview-users/')
        },
        emailPreviewSample (data) {
            return api.$post('/api/extras/email-preview-sample/', data)
        },
        generateBase64 (data) {
            return api.$post('/api/extras/generate-base64/', data)
        },
        getExistingClientToken (data) {
            return api.$post('/api/scheduling/client-token/', data)
        },
        // Allowed consult methods for an existing client by id (admin scheduler
        // paying-source gate). Returns { allowed_methods: [...] }.
        getClientAllowedMethods (userId, params = {}) {
            return api.$post('/api/scheduling/client-allowed-methods/', { user_id: userId, ...params })
        },
        // The signed-in client's own allowed methods, resolved from the session
        // rather than an id. Returns { allowed_methods: [...] }, or
        // { allowed_methods: null } when the caller isn't a client (an agent
        // previewing wizard mode has no eligibility of their own).
        getMyAllowedMethods (params = {}) {
            return api.$get('/api/scheduling/my-allowed-methods/', { params })
        },
        // eslint-disable-next-line camelcase
        // #727/#730: `open_state_rule: true` asks for the allow-listed advisors only.
        getAgentsForLocation ({ state, fips, active_ok, open_state_rule }) {
            return api.$post('/api/scheduling/agents/', { state, fips, active_ok, open_state_rule })
        },
        getOpenStates () {
            return api.$get('/api/scheduling/open-states/')
        },
        // Single-agent lookup by id or crm_id. Public so the scheduler's ?agent pin
        // works for anon visitors; AgentSelect uses it too (the old /accounts/agent/
        // route was removed in 343b0174 and fell through to the SPA catch-all).
        getSchedulingAgent (idOrCrmId) {
            return api.$get(`/api/scheduling/agent/${idOrCrmId}/`)
        },
        getSchedulingAvailableDates (data) {
            return api.$post('/api/scheduling/dates/', data)
        },
        getSchedulingAvailableTimes (data) {
            return api.$post('/api/scheduling/times/', data)
        },
        bookSchedulingTime (data) {
            return api.$post('/api/scheduling/book/', data)
        },
        // Token-scoped management of a single booked appointment (no login).
        getScheduledAppointment (token) {
            return api.$get('/api/scheduling/scheduled/', { params: { token } })
        },
        cancelScheduledAppointment (token) {
            return api.$post('/api/scheduling/scheduled/cancel/', { token })
        },
        rescheduleScheduledAppointment (data) {
            return api.$post('/api/scheduling/scheduled/reschedule/', data)
        },
        // Re-send the confirmation + pre-consult survey for an existing
        // appointment (wizard "Keep my current time", #576). No Slack, no re-book.
        resendScheduledAppointment (token) {
            return api.$post('/api/scheduling/scheduled/resend/', { token })
        },
        // #648: the post-consultation feedback survey. Public and token-scoped,
        // like /scheduled: no login, no session.
        getConsultFeedback (token) {
            return api.$get('/api/consults/feedback/', { params: { token } })
        },
        submitConsultFeedback (data) {
            return api.$post('/api/consults/feedback/', data)
        },
        // #648: Brokerage HR Dashboard (admin only).
        getFeedbackSummary (params) {
            return api.$get('/api/consults/feedback/admin/summary/', { params })
        },
        getFeedbackResponses (params) {
            return api.$get('/api/consults/feedback/admin/responses/', { params })
        },
        makeFeedbackPreviewLink () {
            return api.$post('/api/consults/feedback/admin/preview-link/', {})
        },
        getFeedbackPilotAppointments (params) {
            return api.$get('/api/consults/feedback/admin/pilot-appointments/', { params })
        },
        makeFeedbackPilotLink (data) {
            return api.$post('/api/consults/feedback/admin/pilot-link/', data)
        },
        getFeedbackTestAccounts () {
            return api.$get('/api/consults/feedback/admin/test-accounts/')
        },
        makeFeedbackTestLink (data) {
            return api.$post('/api/consults/feedback/admin/test-link/', data)
        },
        deleteFeedbackResponse (id) {
            return api.$delete(`/api/consults/feedback/admin/responses/${id}/`)
        },
        checkJotformImport (form) {
            return api.$post('/api/consults/feedback/admin/jotform-import/check/', form)
        },
        importJotformFeedback (form) {
            return api.$post('/api/consults/feedback/admin/jotform-import/', form)
        },
        getMyUpcomingAppointment () {
            return api.$get('/api/scheduling/my-upcoming-appointment/')
        },
        // Staff-only: when the written AEP recommendation is due for a survey
        // submitted "today" (the later of Oct 15 and +3 business days).
        // Returns { due_date: 'YYYY-MM-DD' }.
        getAepRecommendationDue () {
            return api.$get('/api/scheduling/aep-recommendation-due/')
        },
        getUpcomingAppointments (data) {
            return api.$post('/api/scheduling/appointments/upcoming/', data)
        },
        cancelAppointment (appointmentId, data) {
            return api.$post(`/api/scheduling/appointments/${appointmentId}/cancel/`, data)
        },
        updateAppointment (appointmentId, data) {
            return api.$patch(`/api/scheduling/appointments/${appointmentId}/`, data)
        },
        getAppointmentsCalendars () {
            return api.$get('/api/scheduling/appointments/calendars/')
        },
        getSurvey ({ id, aep }) {
            return api.$get(`/api/consults/survey/${id ? `${id}/` : ''}${aep ? `?aep=${aep}` : ''}`)
        },
        getSurveyYears () {
            return api.$get('/api/consults/survey/years/')
        },
        getAnonSurvey ({ id, aep }) {
            return api.$get(`/api/consults/survey/anon/${id ? `${id}/` : ''}${aep ? `?aep=${aep}` : ''}`)
        },
        getAsFullservePerOpps (id) {
            return api.$get(`/api/consults/survey/${id}/as-fullserve-per-opps/`)
        },
        getNlRecStatus (id) {
            return api.$get(`/api/consults/survey/${id}/nl-rec-status/`)
        },
        getClient ({ id, aep }) {
            return api.getSurvey({ id, aep })
        },
        getClientForScheduling (id) {
            return api.$get(`/api/accounts/client/${id}/for-scheduling/`)
        },
        getClientSpouseForScheduling (id) {
            return api.$get(`/api/accounts/client/${id}/spouse-for-scheduling/`)
        },
        getClientAgent (id) {
            return api.$get(`/api/accounts/client-agent/${id}/`)
        },
        deactivateClient (id, data = {}) {
            return api.$post(`/api/accounts/deactivate/${id}/`, data)
        },
        reactivateClient (id) {
            return api.$post(`/api/accounts/reactivate/${id}/`)
        },
        refreshClientCaches (id) {
            return api.$post(`/api/accounts/refresh-caches/${id}/`)
        },
        getAgents () {
            return api.$post('/api/consults/agents/')
        },
        updateSurvey ({ id, data }) {
            return api.$patch(`/api/consults/survey/${id ? `${id}/` : ''}`, data)
        },
        // #739: the client changes bar under the advisor's client tabs
        getClientChanges (id) {
            return api.$get(`/api/consults/survey/${id}/changes/`)
        },
        openClientChanges (id, days) {
            return api.$post(`/api/consults/survey/${id}/changes/opened/`, { days })
        },
        unreadClientChanges (id, day) {
            return api.$delete(`/api/consults/survey/${id}/changes/opened/${day}/`)
        },
        completeSurvey (data = {}) {
            return api.$post('/api/consults/survey/complete/', data)
        },
        // The /renewal/* step trail (2026-09-22). One call per step ENTRY;
        // see mixins/renewal-page.js and the backend's track_wizard_step.
        trackWizardStep (data) {
            return api.$post('/api/consults/survey/wizard-step/', data)
        },
        agentCompleteSurvey (uid, data = {}) {
            return api.$post(`/api/consults/survey/agent-complete/${uid}/`, data)
        },
        getSummary (id) {
            return api.$get(`/api/consults/summary/${id ? `${id}/` : ''}`)
        },
        // the newest frozen copy of a sent summary; 404 = none, show the live page
        getSummaryCopy (id) {
            return api.$get(`/api/consults/summary/${id}/copy/`)
        },
        resetSummary ({ id, data }) {
            return api.$patch(`/api/consults/summary/${id}/reset`, { data })
        },
        updateSummary ({ id, data }) {
            return api.$patch(`/api/consults/summary/${id}/`, { data })
        },
        uploadSummaryFile ({ id, data }) {
            return api.$post(`/api/consults/summary/${id}/files`, { data })
        },
        deleteSummaryFile ({ id, data }) {
            return api.$delete(`/api/consults/summary/${id}/files`, { data })
        },
        createSummary (data) {
            return api.$post('/api/consults/summary/', data)
        },
        sendSummary ({ id, data }) {
            return api.$post(`/api/consults/summary/${id}/send/`, data)
        },
        sendPreConsultSurvey ({ id, data }) {
            return api.$post(`/api/consults/send-pre-consult-survey/${id}/`, data)
        },
        sendAepSurvey ({ id, data }) {
            return api.$post(`/api/consults/send-aep-survey/${id}/`, data)
        },
        keepPlans (body) {
            return api.$post('/api/consults/keep-plans/')
        },
        agentKeepPlans (body) {
            return api.$post('/api/consults/agent-keep-plans/', body)
        },
        startEnrollment (body) {
            return api.$post('/api/consults/start-enrollment/', body)
        },
        agentStartEnrollment (body) {
            return api.$post('/api/consults/agent-start-enrollment/', body)
        },
        getPlan (id) {
            return api.$get(`/api/plan/${id}/`)
        },
        getGroup (id, params = {}) {
            return api.$get(`/api/accounts/group/${id}/`, { params })
        },
        getGroupSchedulingMethods (id, params = {}) {
            return api.$get(`/api/scheduling/group/${id}/methods/`, { params })
        },
        updateGroup (group) {
            return api.$patch(`/api/accounts/group/${group?.id}/`, group)
        },
        getGroupWebinars (id, params = {}) {
            return api.$get(`/api/accounts/group/${id}/webinars/`, { params })
        },
        getGroupDrugs (id, params = {}) {
            return api.$get(`/api/accounts/group/${id}/drugs/`, { params })
        },
        deactivateGroup (id) {
            return api.$post(`/api/accounts/group/deactivate/${id}/`)
        },
        reactivateGroup (id) {
            return api.$post(`/api/accounts/group/reactivate/${id}/`)
        },
        getSummaryFiles (id) {
            return api.$get(`/api/consults/summary/${id}/files/`)
        },
        roseAiSendChatMessage (body) {
            return api.$post(`/api/extras/roseai/chat/messages/${body?.uid}/`, body)
        },
        roseAiUploadChatFiles (form = {}, params = {}) {
            return api.$post(`/api/extras/roseai/chat/upload/${params?.uid}/files/`, form, { params })
        },
        roseAiGetChatMessages (id, params = {}) {
            return api.$get(`/api/extras/roseai/chat/messages/${id}/`, { params })
        },
        roseAiClearChatMessages (id) {
            return api.$delete(`/api/extras/roseai/chat/messages/${id}/`)
        },
        roseAiCreateCompletion (data) {
            return api.$post('/api/extras/roseai/completion/', data)
        },
        aiCreateCompletion (data) {
            return api.$post('/api/extras/ai/completion/', data)
        },
        uploadSentryShots (form) {
            return api.$post('/api/extras/sentry-shots/', form)
        },
        uploadFiles (form = {}, params = {}) {
            return api.$post('/api/extras/files/', form, { params })
        },
        deleteFile (id) {
            return api.$delete(`/api/extras/files/${id}/`)
        },
        updateFile (file) {
            return api.$put(`/api/extras/files/${file.file_id || file.id}/`, file?.metadata?.available_states || [])
        },
        // Plan files live in Oracle (see drschoice/flow#592). They must use the
        // /plan-files/ endpoints, NOT /files/ (Flow's UploadedFile) — an Oracle
        // plan-file id sent to /files/ collides with an unrelated Flow file.
        deletePlanFile (id) {
            return api.$delete(`/api/extras/plan-files/${id}/`)
        },
        updatePlanFile (file) {
            return api.$put(`/api/extras/plan-files/${file.file_id || file.id}/`, file?.metadata?.available_states || [])
        },
        createGroupPlan (body) {
            return api.$post('/api/extras/group-plans/', body)
        },
        updateGroupPlan (body) {
            return api.$patch('/api/extras/group-plans/', body)
        },
        searchGroupPlans (params) {
            return api.$get('/api/extras/group-plans/', { params })
        },
        getGroupPlansFromCopays (slug, params) {
            return api.$get(`/api/proxies/oracle/drug_cost/groups/${slug}/plans/`, { params })
        },
        getAllGroupPlanFiles (params) {
            return api.$get('/api/extras/files/all/', { params })
        },
        updateUserGroupPlan (body) {
            return api.$patch('/api/extras/user-group-plans/', body)
        },
        searchUserGroupPlans (params) {
            return api.$get('/api/extras/user-group-plans/', { params })
        },
        getAllUserGroupPlans (params) {
            return api.$get('/api/extras/user-group-plans/all/', { params })
        },
        getAllGroupPlans (params) {
            return api.$get('/api/extras/group-plans/all/', { params })
        },
        getUserGroupPlan (id) {
            return api.$get(`/api/extras/user-group-plans/${id}/`)
        },
        getGroupPlan (id) {
            return api.$get(`/api/extras/group-plans/${id}/`)
        },
        deleteGroupPlan (id) {
            return api.$delete(`/api/extras/group-plans/${id}/`)
        },
        updatePlanExtras (body) {
            return api.$post('/api/extras/plans/', body)
        },
        getPlanNotes (params) {
            return api.$get('/api/extras/plan-notes/', { params })
        },
        createPlanNote (body) {
            return api.$post('/api/extras/plan-notes/', body)
        },
        updatePlanNote (id, body) {
            return api.$put(`/api/extras/plan-notes/${id}/`, body)
        },
        deletePlanNote (id) {
            return api.$delete(`/api/extras/plan-notes/${id}/`)
        },
        // Plan Notes page (pages/plan-notes.vue): oracle's plan_note_search.py
        // reads. `carrier` is a list (every catalog variant behind the picked
        // name), so it goes as repeated keys.
        searchPlanNotes (params) {
            return api.$get('/api/extras/plan-notes/search/', { params, paramsSerializer: repeatKeys })
        },
        searchPlanNotePlans (params) {
            return api.$get('/api/extras/plan-notes/plans/', { params, paramsSerializer: repeatKeys })
        },
        getPlanNoteSubtypes (params) {
            return api.$get('/api/extras/plan-notes/subtypes/', { params })
        },
        getItem (key) {
            return api.$get(`/api/extras/item/${key}/`)
        },
        getItems (params) {
            return api.$get('/api/extras/items/', { params })
        },
        // the frontend build the server serves now (plugins/flow-update.js)
        getBuild () {
            return api.$get('/api/extras/build/', { spinner: false, alert: false })
        },
        setItem (key, value, options = {}) {
            return api.$post(`/api/extras/item/${key}/`, { value, ...options })
        },
        deleteItem (key) {
            return api.$delete(`/api/extras/item/${key}/`)
        },
        getGroupReport (params) {
            return api.$get('/api/accounts/group-report/', { params })
        },
        emailGroupReport (body) {
            return api.$post('/api/accounts/email-group-report/', body)
        },
        getGroups (params) {
            return api.$get('/api/accounts/group/', { params })
        },
        findGroup (params) {
            return api.$get('/api/accounts/find/group/', { params })
        },
        searchCommissions (params) {
            if (params.group_by) {
                return api.$get('/api/accounts/commissions-grouped/', { params })
            }
            return api.$get('/api/accounts/commissions/', { params })
        },
        getCrmOpportunities (params) {
            return api.$get('/api/consults/crm-opportunities/', { params })
        },
        updateCrmOpportunities (data) {
            return api.$patch('/api/consults/crm-opportunities/', data)
        },
        updateCrmOpportunitiesCorrectedProducts (data) {
            return api.$patch('/api/consults/crm-opportunities-correct-products/', data)
        },
        updateCrmOpportunitiesCorrectedV2Products (data) {
            return api.$patch('/api/consults/crm-opportunities-correct-v2-products/', data)
        },
        getCrmOpportunitiesStages (params = {}) {
            return api.$get('/api/consults/crm-opportunities/stages/', { params })
        },
        getCommands () {
            return api.$get('/api/accounts/commands/')
        },
        clearCurrentCommands () {
            return api.$delete('/api/accounts/commands/')
        },
        getCurrentCommands () {
            return api.$get('/api/accounts/commands/current/')
        },
        executeCommand (body) {
            return api.$post('/api/accounts/command/', body)
        },
        getActivityChoices () {
            return api.$get('/api/accounts/activity-choices/')
        },
        getActivity (params) {
            return api.$get('/api/accounts/activity/', { params })
        },
        getActivityAll (params) {
            return api.$get('/api/accounts/activity-all/', { params })
        },
        getSpouse (id) {
            return api.$get(`/api/accounts/spouse/${id}/`)
        },
        getRelatedClients (id) {
            return api.$get(`/api/accounts/related-clients/${id}/`)
        },
        addRelatedClient (id, data) {
            return api.$post(`/api/accounts/related-clients/${id}/`, data)
        },
        deleteRelatedClient ({ id1, id2 }) {
            return api.$delete(`/api/accounts/related-clients/${id1}/${id2}/`)
        },
        getCrmClient (id) {
            return api.$get(`/api/accounts/crm-client/${id}/`)
        },
        getMatchingClients (id) {
            return api.$get(`/api/accounts/matching-clients/${id}/`)
        },
        getActiveCall () {
            return api.$get('/api/accounts/active-call/')
        },
        getOpportunities (id) {
            return api.$get(`/api/accounts/opportunities/${id ? `${id}/` : ''}`)
        },
        getCmsSsaOpportunities (id) {
            return api.$get(`/api/accounts/cms_ssa_opportunities/${id ? `${id}/` : ''}`)
        },
        ensureCmsSsaOpportunities (id, data) {
            return api.$post(`/api/accounts/cms_ssa_opportunities/${id ? `${id}/` : ''}ensure/`, data)
        },
        getActiveProducts (id) {
            return api.$get(`/api/accounts/active-products/${id ? `${id}/` : ''}`)
        },
        getGroupDashboard (params) {
            return api.$get('/api/accounts/group-dashboard/', { params })
        },
        getBrokerGroups () {
            return api.$get('/api/accounts/broker-groups/')
        },
        getBrokerUserGroups (userId) {
            return api.$get(`/api/accounts/broker/${userId}/groups/`)
        },
        addBrokerGroup (userId, groupId) {
            return api.$post(`/api/accounts/broker/${userId}/groups/`, { group_id: groupId })
        },
        removeBrokerGroup (userId, groupId) {
            return api.$delete(`/api/accounts/broker/${userId}/groups/${groupId}/`)
        },

        adminCreateUser (data) {
            return api.$post('/api/accounts/admin/user/', data)
        },
        adminGetUser (userId) {
            return api.$get(`/api/accounts/admin/user/${userId}/`)
        },
        adminUpdateUser (userId, data) {
            return api.$patch(`/api/accounts/admin/user/${userId}/`, data)
        },
        adminResetPassword (userId) {
            return api.$post(`/api/accounts/admin/user/${userId}/reset-password/`)
        },
        getAgentSettings () {
            return api.$get('/api/accounts/agent-settings/')
        },
        updateAgentSettings (data) {
            return api.$patch('/api/accounts/agent-settings/', data)
        },

        searchClients (params) {
            return api.$get('/api/accounts/client/', { params })
        },
        getRecentClients () {
            return api.$get('/api/accounts/client/recent/')
        },
        searchMatchingClients (params) {
            return api.$get('/api/accounts/matching-clients/', { params })
        },
        searchDuplicateClients (params) {
            return api.$get('/api/accounts/duplicate-clients/', { params })
        },
        searchAgents (params) {
            return api.$get('/api/accounts/agents/', { params })
        },
        // eslint-disable-next-line camelcase
        getQuotes ({ id, year, fresh, detailed, supported = false, light = false, commissionable_only = false, progress_id }) {
            return api.$get(`/api/consults/quotes/${id ? `${id}/` : ''}`, { params: { year, fresh, detailed, supported, light, commissionable_only, progress_id }, timeout: 180000 })
        },
        getQuoteProgress (progressId) {
            // polled every second; the next poll comes sooner than a slow answer
            return api.$get(`/api/consults/quotes/progress/${progressId}/`, { timeout: 3000 })
        },
        getSetting ({ key }) {
            return api.$get(`/api/settings/setting/${key}/`)
        },
        setSetting ({ key, value }) {
            return api.$post(`/api/settings/setting/${key}/`, { value })
        },
        getCountiesForZip (zipcode) {
            return api.$get(`/api/proxies/oracle/counties_for_zip/${zipcode}/`)
        },
        getTimezoneForZip (zipcode) {
            return api.$get(`/api/proxies/oracle/timezone_for_zip/${zipcode}/`)
        },
        // #689 address autocomplete (Oracle → Google Places (New)). POST bodies on
        // purpose: the typed address must not land in access logs.
        addressAutocomplete (input, sessionToken) {
            return api.$post('/api/proxies/oracle/address_autocomplete/', { input, session_token: sessionToken })
        },
        addressDetails (placeId, sessionToken) {
            return api.$post('/api/proxies/oracle/address_details/', { place_id: placeId, session_token: sessionToken })
        },
        searchProviders (params) {
            return api.$get('/api/proxies/oracle/provider_search/', { params })
        },
        searchProviders3 (params) {
            return api.$get('/api/proxies/oracle/provider_search3/', { params })
        },
        searchPharmacies (params) {
            return api.$get('/api/proxies/oracle/pharmacy_search/', { params })
        },
        searchHospitals (params, config) {
            return api.$get('/api/proxies/oracle/hospital_search/', { params, ...config })
        },
        // Identity verification for magic-link sessions. Every comparison is
        // server-side — the survey payload holds the date of birth and ZIP, so
        // a browser-side check would be guarding a secret the page already has.
        // See flow/accounts/identity.py.
        getIdentityStatus () {
            return api.$get('/api/accounts/identity/status/')
        },
        verifyIdentity (data) {
            return api.$post('/api/accounts/identity/verify/', data)
        },
        sendIdentityOtp () {
            return api.$post('/api/accounts/identity/otp/send/')
        },
        verifyIdentityOtp (data) {
            return api.$post('/api/accounts/identity/otp/verify/', data)
        },
        // A magic link held because this browser was signed in as someone else
        // (pages/link-switch.vue).
        getLinkSwitch () {
            return api.$get('/api/accounts/link-switch/')
        },
        confirmLinkSwitch () {
            return api.$post('/api/accounts/link-switch/')
        },
        cancelLinkSwitch () {
            return api.$delete('/api/accounts/link-switch/')
        },
        // Carrier/plan catalog lookup for the renewal wizard's current-plan
        // step (#656). `params`: { year, types, fips, state, q } for carriers,
        // plus { carrier, limit } for plans.
        searchPlanCarriers (params, config) {
            return api.$get('/api/proxies/oracle/plan_carriers/', { params, ...config })
        },
        searchPlans (params, config) {
            return api.$get('/api/proxies/oracle/plan_search/', { params, ...config })
        },
        // #721 round 2: the client's stored current plan ids resolved to a
        // summary year through oracle_spa.plan (agent-only).
        getAepCurrentPlans ({ id, year }) {
            return api.$get(`/api/consults/current-plans/${id}/`, { params: { year } })
        },
        searchDrugs (params) {
            return api.$get('/api/proxies/oracle/drug_search/', { params })
        },
        getDrugOptions ({ year, rxcui, ...params }) {
            return api.$get(`/api/proxies/oracle/drug_options/${year}/${rxcui}/`, { params })
        },
        getDrugCosts (params) {
            // the browser's anonymous ID, so the employer report can count one
            // person's lookups as one (util/visitor-id.js)
            const visitorId = getVisitorId()
            return api.$get('/api/proxies/oracle/drug_cost/', { params: visitorId ? { ...params, visitor_id: visitorId } : params })
        },
        searchConditions (params) {
            return api.$get('/api/proxies/oracle/conditions_search/', { params })
        },
        searchProcedures (params) {
            return api.$get('/api/proxies/oracle/procedures_search/', { params })
        },
        getProcedureCost (code) {
            return api.$get('/api/proxies/oracle/procedure_costs/', { params: { code } })
        },
        getUsStates () {
            return api.$get('/api/consults/survey/states/')
        },
        slackConnect () {
            return api.$get('/api/accounts/slack-auth/start/')
        },
        doubleCheckProviders (data) {
            return api.$post('/api/proxies/oracle/providers_check/', data)
        },
        doubleCheckPrice (data) {
            return api.$post('/api/proxies/oracle/carrier_price/', data)
        },
        getSignedSoas (uid, params) {
            return api.$get(`/api/consults/survey${uid ? `/${uid}/` : '/'}signed-soas/`, { params })
        },
        getSignedSoa (uid, sid, params) {
            return api.$get(`/api/consults/survey${uid ? `/${uid}/` : '/'}signed-soas/${sid}/`, { params })
        },
        createSignedSoa (data) {
            return api.$post('/api/consults/survey/signed-soas/', data)
        },
        createSignedSoaDocumenso (data) {
            return api.$post('/api/consults/survey/signed-soas/documenso/', data)
        },
        replaceSignedSoa (sid, data) {
            return api.$post(`/api/consults/survey/signed-soas/${sid}/replace/`, data)
        },
        // Agent-side: countersigns a client's SOA still awaiting its agent (or
        // whose countersignature failed to file), as the caller, dated today.
        countersignSoa (uid, sid) {
            return api.$post(`/api/consults/survey/${uid}/signed-soas/${sid}/countersign/`)
        },
        // Admin-only server-side (@require_admin); deletes the db row and the
        // Documenso envelope. `uid` is required — there is no client-facing
        // delete, by design.
        deleteSignedSoa (uid, sid) {
            return api.$delete(`/api/consults/survey/${uid}/signed-soas/${sid}/`)
        },
        signedSoaPdfUrl (uid, sid) {
            return `/api/consults/survey${uid ? `/${uid}/` : '/'}signed-soas/${sid}/download/`
        },
        requestEligibilityCheck (data) {
            return api.$post('/api/scheduling/eligibility-request/', data)
        },
        async getEnrollmentPeriodRecommendation (id) {
            const res = await api.$get(`/api/extras/ai/recommend-enrollment-period/${id}/?user=${id}`)
            if (res) {
                return res.choices[0].message.content
            } else {
                return 'Failed to get enrollment period recommendation'
            }
        },
        getRecentAppointments (params) {
            return api.$get('/api/accounts/activity/recent-appointments/', { params })
        },
        async getRecentUpdateSurveyActivities (uid, options = {}) {
            options.limit = options.limit ?? 3
            const query = options.query || null
            const { items } = await api.getActivity({
                query,
                activity: 'update_survey,intake',
                client_id: uid,
                size: options.limit,
            })
            return items
        },
        async getRecentSchedulerActivitiesContexts (uid, options = {}) {
            options.limit = options.limit ?? 3
            options.fetchFiles = options.fetchFiles ?? true
            const { items } = await api.getActivity({ activity: 'schedule', client_id: uid, size: options.limit })
            let contexts = items.map(a => a.context)
            try {
                contexts = await Promise.all(items.map(a => a.context?.file && options.fetchFiles ? this.getActivityContext(a.id) : a.context))
            } catch (e) {}
            return contexts
        },
        listApplicationTokens () {
            return api.$get('/api/extras/application-token/list')
        },
        createApplicationToken (data) {
            return api.$post('/api/extras/application-token/create', data)
        },
        deleteApplicationToken (data) {
            return api.$delete('/api/extras/application-token/delete', { data })
        },
        async getActivityContext (activityId) {
            const res = await api.$get(`/api/accounts/activity/${activityId}/context.json`, { spinner: false, alert: false })
            if (!res?.error) {
                return res
            }
        },
    }
    return api
}

export default (context, inject) => {
    const $api = createApi(context)
    inject('api', $api)
}
